<template>
  <div class="runs-list">
    <div class="runs-list__toolbar">
      <button class="button-light" @click="toggleShowDeleted">
        {{ showDeleted ? 'Hide Deleted' : 'Show Deleted' }}
      </button>
    </div>
    <div v-if="loading" class="loading">Loading tasks...</div>
    <div v-else-if="filteredTaskList.length > 0" class="tasks-container">
      <TaskCard
        v-for="task in filteredTaskList"
        :key="task.id"
        :session="task"
        :permissions="{ trainer_write: true }"
        :show-icons="true"
        @delete-session="handleTaskDeleted"
      />
    </div>
    <div v-else class="empty-state">
      No tasks available
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeUnmount, watch,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import TaskCard from '@/components/Apps/TaskCard.vue';

const props = defineProps({
  train_session_id: {
    type: [String, Number],
    required: false,
  },
  val_session_id: {
    type: [String, Number],
    required: false,
  },
});

const taskList = ref([]);
const loading = ref(false);
const showDeleted = ref(false);
const dataConnect = new DatastoreConnect();

const taskInfoSocket = ref(null);
let taskInfoReconnectTimeout = null;
let isIntentionalTaskInfoSocketClose = false;

const isDeletedTask = (task) => {
  const statuses = [
    task?.status,
    task?.status_info?.status,
    task?.docker_task?.status,
  ].filter(Boolean);

  return statuses.some((status) => String(status).toLowerCase().includes('deleted'));
};

const filteredTaskList = computed(() => {
  if (showDeleted.value) {
    return taskList.value;
  }

  return taskList.value.filter((task) => !isDeletedTask(task));
});

const fetchTasks = async () => {

  let args=null;
  if (props.train_session_id) {
    args={train_session_id: Number(props.train_session_id)};
  } 
  if (props.val_session_id) {
    args={val_session_id: Number(props.val_session_id)};
  }

  if (!args) {
    console.warn('No session ID provided for fetching tasks.');
    return;
    }
  
  loading.value = true;
  try {
    const response = await dataConnect.api('task.list', args);
    
    if (response && response.result) {
      taskList.value = response.result.tasks || [];
    } else {
      taskList.value = [];
    }
  } catch (error) {
    console.error('Error fetching tasks:', error);
    taskList.value = [];
  } finally {
    loading.value = false;
  }

  if (getSubscribedTaskIds().length > 0) {
    connectTaskInfoSocket();
    sendTaskInfoSubscription();
  } else {
    disconnectTaskInfoSocket();
  }
};

// Task info websocket (keeps run task cards live, same pattern as the trainer session list page)

const getTaskInfoWebSocketEndpoint = () => '/ws/task_info';

const getTaskInfoWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // Same-origin socket allows auth cookie to be sent in the handshake.
  return `${protocol}://${window.location.host}${getTaskInfoWebSocketEndpoint()}`;
};

const getSubscribedTaskIds = () => taskList.value
  .map((task) => Number(task?.id))
  .filter((taskId) => Number.isInteger(taskId) && taskId > 0);

const clearTaskInfoReconnectTimeout = () => {
  if (taskInfoReconnectTimeout) {
    clearTimeout(taskInfoReconnectTimeout);
    taskInfoReconnectTimeout = null;
  }
};

const scheduleTaskInfoReconnect = () => {
  clearTaskInfoReconnectTimeout();
  taskInfoReconnectTimeout = setTimeout(() => {
    connectTaskInfoSocket();
  }, 10000);
};

const extractTaskInfoUpdates = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === 'object' && Number.isInteger(Number(payload.id))) {
    return [payload];
  }

  if (Array.isArray(payload?.tasks)) {
    return payload.tasks;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (payload?.task && typeof payload.task === 'object') {
    return [payload.task];
  }

  return [];
};

const applyTaskInfoUpdates = (updates) => {
  if (!Array.isArray(updates) || updates.length === 0) {
    return;
  }

  const updatesById = new Map(
    updates
      .map((update) => [Number(update?.id), update])
      .filter(([taskId]) => Number.isInteger(taskId) && taskId > 0)
  );

  if (updatesById.size === 0) {
    return;
  }

  taskList.value = taskList.value.map((task) => {
    const taskId = Number(task?.id);
    const update = updatesById.get(taskId);
    if (!update) {
      return task;
    }

    const statusUpdateRaw = update?.status_update ?? update?.statis_update ?? update?.statusUpdate;
    const statusUpdate = statusUpdateRaw && typeof statusUpdateRaw === 'object' ? statusUpdateRaw : null;

    const mergedStatusInfo = statusUpdate ? {
      ...(task.status_info || {}),
      status: statusUpdate.status ?? task.status_info?.status,
      message: statusUpdate.message ?? task.status_info?.message,
      label: statusUpdate.label ?? task.status_info?.label,
      stage_name: statusUpdate.stage ?? task.status_info?.stage_name,
      stage_label: statusUpdate.label ?? task.status_info?.stage_label,
      percent: statusUpdate.percentage ?? task.status_info?.percent,
      stage_number: statusUpdate.stage_number ?? task.status_info?.stage_number,
      total_stages: statusUpdate.total_stages ?? task.status_info?.total_stages,
    } : task.status_info;

    return {
      ...task,
      status: update.status ?? update.name ?? task.status,
      message: statusUpdate?.message ?? update.message ?? task.message,
      status_info: mergedStatusInfo,
      status_process_info: update.status_process_info ?? task.status_process_info,
      common_app_params: update.common_app_params ?? task.common_app_params,
      app_info: update.app_info ?? task.app_info,
    };
  });
};

const sendTaskInfoSubscription = (taskIds = getSubscribedTaskIds()) => {
  if (!taskInfoSocket.value || taskInfoSocket.value.readyState !== WebSocket.OPEN) {
    return;
  }

  taskInfoSocket.value.send(JSON.stringify({ task_ids: taskIds }));
};

const disconnectTaskInfoSocket = () => {
  clearTaskInfoReconnectTimeout();
  isIntentionalTaskInfoSocketClose = true;

  if (taskInfoSocket.value) {
    if (taskInfoSocket.value.readyState === WebSocket.OPEN || taskInfoSocket.value.readyState === WebSocket.CONNECTING) {
      taskInfoSocket.value.close();
    }
    taskInfoSocket.value = null;
  }
};

const connectTaskInfoSocket = () => {
  if (taskInfoSocket.value) {
    if (taskInfoSocket.value.readyState === WebSocket.OPEN) {
      sendTaskInfoSubscription();
      return;
    }
    if (taskInfoSocket.value.readyState === WebSocket.CONNECTING || taskInfoSocket.value.readyState === WebSocket.CLOSING) {
      return;
    }
  }

  clearTaskInfoReconnectTimeout();
  isIntentionalTaskInfoSocketClose = false;

  const socket = new WebSocket(getTaskInfoWebSocketUrl());
  taskInfoSocket.value = socket;

  socket.onopen = () => {
    sendTaskInfoSubscription();
  };

  socket.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);

      if (payload?.message_type === 'error' || payload?.error) {
        console.error('Task info stream error:', payload?.message || payload?.error);
        return;
      }

      applyTaskInfoUpdates(extractTaskInfoUpdates(payload));
    } catch (err) {
      console.error('Failed to parse task info frame:', err);
    }
  };

  socket.onclose = () => {
    taskInfoSocket.value = null;
    if (!isIntentionalTaskInfoSocketClose && getSubscribedTaskIds().length > 0) {
      scheduleTaskInfoReconnect();
    }
  };

  socket.onerror = () => {
    // Reconnect is handled by onclose.
  };
};

watch(
  () => [props.train_session_id, props.val_session_id],
  () => {
    if (props.train_session_id || props.val_session_id) {
      fetchTasks();
    }
  }
);

onMounted(() => {
  fetchTasks();
});

onBeforeUnmount(() => {
  disconnectTaskInfoSocket();
});

const handleTaskDeleted = () => {
  // Refresh the task list when a task is deleted
  fetchTasks();
};

const toggleShowDeleted = () => {
  showDeleted.value = !showDeleted.value;
};
</script>

<style lang="scss" scoped>
.runs-list {
  padding: 16px;
}

.runs-list__toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.loading {
  text-align: center;
  padding: 20px;
  color: var(--body-text-color-secondary);
}

.tasks-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--body-text-color-secondary);
}
</style>
